'use client'

import type { NavigationMobileLevelProps } from '@maestro/commerce-blocks/components/navigation-header/navigation-header.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React, { useCallback, useMemo } from 'react'

import { HeaderControls } from '@maestro/commerce-blocks/components/header-controls'
import HeaderTopbarCompanySwitcher from '@maestro/commerce-blocks/components/header-topbar/header-topbar-company-switcher'
import { NavigationMobileShortcuts } from '@maestro/commerce-blocks/components/navigation-header'
import { useMobileNavigation } from '@maestro/commerce-blocks/providers/mobile-navigation/context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'
import { Link } from '@pearlconvert/ui/components/link'

export default function NavigationMobileLevel(
  props: NavigationMobileLevelProps
) {
  const { node, menu, shortcuts } = props
  const { openLevel, isActive, isChildActive, activeNodeIds } =
    useMobileNavigation()
  const { publish } = useEventBusContext()
  const t = useTranslations('commerce-blocks')

  const {
    header: { isCompanySwitcherEnabled },
    cms: { navigation },
  } = theme()

  // Polar menus fetched from Sanity are prefixed with domain and i18n.
  // But the logic to define if mobile menu is active or not is based on theme setting navigation id
  const levelId = menu ? navigation.header : (node?._key ?? '')

  const menuPath = useMemo(() => {
    return {
      currentLevel: activeNodeIds.length,
      level1: activeNodeIds[0] || undefined,
      level2: activeNodeIds[1] || undefined,
      level3: activeNodeIds[2] || undefined,
    }
  }, [activeNodeIds])

  const handleClickMenu = useCallback(
    (hasChildren: boolean, clickedTitle?: string) => {
      const { level1, level2, level3, currentLevel } = menuPath

      if (hasChildren) {
        return
      }

      let menuLevel1 = level1
      let menuLevel2 = level2
      let menuLevel3 = level3

      if (currentLevel === 1) {
        menuLevel1 = clickedTitle || undefined
        menuLevel2 = undefined
        menuLevel3 = undefined
      } else if (currentLevel === 2) {
        menuLevel2 = clickedTitle || undefined
        menuLevel3 = undefined
      } else if (currentLevel === 3) {
        menuLevel3 = clickedTitle || undefined
      }

      publish(EventType.MENU_CLICKED, {
        level1: menuLevel1,
        level2: menuLevel2,
        level3: menuLevel3,
      })
    },
    [menuPath, publish]
  )

  return (
    <ul
      className={classNames('nav-mobile__root', {
        'nav-mobile__root--opened': isActive(levelId),
        'nav-mobile__root--child-opened':
          !isActive(levelId) && isChildActive(levelId),
        'nav-mobile__root--closed':
          !isActive(levelId) && !isChildActive(levelId),
      })}
    >
      {node && (
        <>
          <li className="nav-mobile__item nav-mobile__item--current">
            {node.title}
          </li>
          <li className="nav-mobile__item nav-mobile__item--all">
            <Link
              className="nav-mobile__link nav-mobile__link--all"
              href={node.url}
              target={node.target}
              aria-label={t('navigation-header.aria-all-in', {
                title: node.title,
              })}
              onClick={() => handleClickMenu(false, node.title)}
            >
              {t('navigation-header.all-in', { title: node.title })}
            </Link>
          </li>
        </>
      )}
      {(menu || node)?.children?.map((node) => (
        <li
          className={`nav-mobile__item${
            activeNodeIds.length === 1 ? ' nav-mobile__item--top' : ''
          }`}
          key={node._key}
        >
          {node.children ? (
            <button
              className={classNames('nav-mobile__button', node.cssClass, {
                'nav-mobile__button--top': activeNodeIds.length === 1,
                'nav-mobile__button--highlight': node.highlight,
              })}
              onClick={() => {
                handleClickMenu(true, node.title)
                openLevel(node._key)
              }}
            >
              {node.title}
            </button>
          ) : (
            <Link
              className={classNames('nav-mobile__link', node.cssClass, {
                'nav-mobile__link--top': activeNodeIds.length === 1,
                'nav-mobile__link--highlight': node.highlight,
              })}
              href={node.url}
              target={node.target}
              onClick={() => handleClickMenu(false, node.title)}
            >
              {node.title}
            </Link>
          )}

          {node.children && <NavigationMobileLevel node={node} />}
        </li>
      ))}
      {shortcuts && <NavigationMobileShortcuts shortcuts={shortcuts} />}
      {menu && (
        <>
          <li className="nav-mobile__item nav-mobile__item--user">
            {isCompanySwitcherEnabled && <HeaderTopbarCompanySwitcher />}
          </li>
          <li className="nav-mobile__item">
            <HeaderControls />
          </li>
        </>
      )}
    </ul>
  )
}
